/**
 * Notification Service
 * Handles desktop notifications with permission management and service worker integration
 */

import { pwaService } from './pwa.service';

class NotificationService {
  constructor() {
    this.permission = Notification.permission;
    this.listeners = {
      permissionChange: []
    };
  }

  /**
   * Check if notifications are supported
   * @returns {boolean}
   */
  isSupported() {
    return 'Notification' in window;
  }

  /**
   * Get current permission status
   * @returns {string} 'granted', 'denied', or 'default'
   */
  getPermission() {
    return Notification.permission;
  }

  /**
   * Check if notifications are enabled
   * @returns {boolean}
   */
  isEnabled() {
    return this.isSupported() && Notification.permission === 'granted';
  }

  /**
   * Request notification permission
   * @returns {Promise<string>} Permission status
   */
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('[Notification] Notifications not supported');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      console.warn('[Notification] Notifications blocked by user');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      this._emit('permissionChange', permission);
      console.log('[Notification] Permission:', permission);
      return permission;
    } catch (error) {
      console.error('[Notification] Permission request failed:', error);
      return 'denied';
    }
  }

  /**
   * Show a notification using the Notification API directly
   * @param {string} title - Notification title
   * @param {Object} options - Notification options
   * @returns {Notification|null}
   */
  show(title, options = {}) {
    if (!this.isEnabled()) {
      console.warn('[Notification] Notifications not enabled');
      return null;
    }

    const defaultOptions = {
      icon: '/android-icon-192x192.png',
      badge: '/android-icon-96x96.png',
      silent: false,
      timestamp: Date.now(),
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (options.onClick) {
          options.onClick(event);
        }
        if (options.url) {
          window.location.href = options.url;
        }
        notification.close();
      };

      notification.onclose = () => {
        if (options.onClose) {
          options.onClose();
        }
      };

      notification.onerror = (error) => {
        console.error('[Notification] Error:', error);
        if (options.onError) {
          options.onError(error);
        }
      };

      return notification;
    } catch (error) {
      console.error('[Notification] Failed to show:', error);
      return null;
    }
  }

  /**
   * Show a notification using the Service Worker (works even when app is in background)
   * @param {string} title - Notification title
   * @param {Object} options - Notification options
   * @returns {Promise<void>}
   */
  async showPersistent(title, options = {}) {
    if (!this.isEnabled()) {
      console.warn('[Notification] Notifications not enabled');
      return;
    }

    const registration = pwaService.getRegistration();
    if (!registration) {
      console.warn('[Notification] No service worker registration');
      // Fallback to regular notification
      return this.show(title, options);
    }

    const defaultOptions = {
      icon: '/android-icon-192x192.png',
      badge: '/android-icon-96x96.png',
      vibrate: [200, 100, 200],
      tag: 'firmwide-notification',
      requireInteraction: false,
      silent: false,
      data: {
        url: options.url || '/'
      },
      actions: [
        { action: 'open', title: 'Otvori' },
        { action: 'close', title: 'Zatvori' }
      ],
      ...options
    };

    try {
      await registration.showNotification(title, defaultOptions);
      console.log('[Notification] Persistent notification shown');
    } catch (error) {
      console.error('[Notification] Failed to show persistent:', error);
      // Fallback to regular notification
      this.show(title, options);
    }
  }

  /**
   * Show a system notification for Firmwide
   * @param {string} body - Notification body
   * @param {Object} options - Additional options
   */
  showSystemNotification(body, options = {}) {
    return this.show('Obavijest sustava Firmwide', {
      body,
      icon: '/android-icon-192x192.png',
      ...options
    });
  }

  /**
   * Show a welcome notification
   * @param {number} unreadCount - Number of unread notifications
   */
  showWelcomeNotification(unreadCount) {
    if (unreadCount > 0) {
      return this.show('Dobrodošli u Firmwide', {
        body: `Imate ${unreadCount} ${unreadCount === 1 ? 'novu obavijest' : 'novih obavijesti'}.`,
        icon: '/android-icon-192x192.png',
        tag: 'firmwide-welcome'
      });
    }
    return null;
  }

  /**
   * Show a task notification
   * @param {Object} task - Task object with title and link
   */
  showTaskNotification(task) {
    return this.show('Nova zadaća', {
      body: task.title,
      icon: '/android-icon-192x192.png',
      tag: 'firmwide-task',
      url: task.link
    });
  }

  /**
   * Subscribe to notification events
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }

  /**
   * Unsubscribe from notification events
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  _emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  /**
   * Close all notifications with a specific tag
   * @param {string} tag - Notification tag
   */
  async closeByTag(tag) {
    const registration = pwaService.getRegistration();
    if (registration) {
      const notifications = await registration.getNotifications({ tag });
      notifications.forEach(notification => notification.close());
    }
  }

  /**
   * Get all active notifications
   * @returns {Promise<Notification[]>}
   */
  async getAll() {
    const registration = pwaService.getRegistration();
    if (registration) {
      return registration.getNotifications();
    }
    return [];
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
